import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
  useTheme
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Notifications as NotificationsIcon,
  Send as SendIcon,
  History as HistoryIcon,
  AccessTime as AccessTimeIcon,
  Event as EventIcon,
} from '@mui/icons-material'

import RoleGuard from '../../components/Auth/RoleGuard'
import NotificationForm from '../../components/Notifications/NotificationForm'
import NotificationLogs from '../../components/Notifications/NotificationLogs'
import useNotificationManagement from '../../hooks/useNotifications'

const NotificationsPage = () => {
  const theme = useTheme()
  const [activeTab, setActiveTab] = useState(0)
  const [formOpen, setFormOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [configToDelete, setConfigToDelete] = useState(null)

  const handleFormSuccess = () => {
    setFormOpen(false)
    setEditingConfig(null)
  }

  const {
    configs,
    isLoading,
    error,
    createConfig,
    updateConfig,
    deleteConfig,
    sendTest,
    sendWeeklyReminder,
    sendOverdueAlert,
    sendMonthlyReport,
    isCreating,
    isUpdating,
    isDeleting,
    isSendingTest,
    isSendingWeekly,
    isSendingOverdue,
    isSendingMonthly
  } = useNotificationManagement(handleFormSuccess)

  console.log('🔍 DEBUG - NotificationsPage - configs:', configs, 'isLoading:', isLoading, 'error:', error);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleCreateConfig = () => {
    setEditingConfig(null)
    setFormOpen(true)
  }

  const handleEditConfig = (config) => {
    setEditingConfig(config)
    setFormOpen(true)
  }

  const handleDeleteConfig = (config) => {
    setConfigToDelete(config)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (configToDelete) {
      deleteConfig(configToDelete.id)
      setDeleteDialogOpen(false)
      setConfigToDelete(null)
    }
  }

  const handleSendTest = (config) => {
    sendTest({
      recipients: config.recipients,
      type: 'test'
    })
  }

  const handleSendWeeklyReminder = () => {
    sendWeeklyReminder()
  }

  const handleSendOverdueAlert = () => {
    sendOverdueAlert()
  }

  const handleSendMonthlyReport = () => {
    sendMonthlyReport()
  }


  const handleToggleConfig = (config) => {
    updateConfig({
      id: config.id,
      configData: { active: !config.active }
    })
  }

  const handleFormSubmit = (formData) => {
    if (editingConfig) {
      updateConfig({ id: editingConfig.id, configData: formData })
    } else {
      createConfig(formData)
    }
    // Don't close immediately, let the mutation's onSuccess callback handle it
  }

  const getNotificationTypeLabel = (type) => {
    const types = {
      'weekly_reminder': 'Recordatorio Semanal',
      'overdue_alert': 'Alerta de Vencidas',
      'monthly_report': 'Reporte Mensual',
      'payment_received': 'Pago Recibido',
      'new_invoice': 'Nueva Factura'
    }
    return types[type] || type
  }

  const getNotificationTypeColor = (type) => {
    const colors = {
      'weekly_reminder': 'primary',
      'overdue_alert': 'error',
      'monthly_report': 'info',
      'payment_received': 'success',
      'new_invoice': 'warning'
    }
    return colors[type] || 'default'
  }

  // Función para obtener colores de chips adaptativos al tema
  const getNotificationTypeColorWithTheme = (type, theme) => {
    const isDark = theme.palette.mode === 'dark'
    const colors = {
      'weekly_reminder': isDark ? 'primary' : 'primary',
      'overdue_alert': isDark ? 'error' : 'error',
      'monthly_report': isDark ? 'info' : 'info',
      'payment_received': isDark ? 'success' : 'success',
      'new_invoice': isDark ? 'warning' : 'warning'
    }
    return colors[type] || 'default'
  }

  const formatCronSchedule = (cron) => {
    if (!cron) return 'No programado'
    
    const schedules = {
      // Diario
      '0 12 * * *': 'Diario 12:00 PM',
      '0 9 * * *': 'Diario 9:00 AM',
      '0 10 * * *': 'Diario 10:00 AM',
      '0 8 * * *': 'Diario 8:00 AM',
      '0 17 * * *': 'Diario 5:00 PM',
      
      // Semanal
      '0 9 * * 1': 'Lunes 9:00 AM',
      '0 9 * * 2': 'Martes 9:00 AM',
      '0 9 * * 3': 'Miércoles 9:00 AM',
      '0 9 * * 4': 'Jueves 9:00 AM',
      '0 9 * * 5': 'Viernes 9:00 AM',
      '0 9 * * 6': 'Sábado 9:00 AM',
      '0 9 * * 0': 'Domingo 9:00 AM',
      '0 17 * * 5': 'Viernes 5:00 PM',
      
      // Mensual
      '0 8 1 * *': 'Mensual (día 1) 8:00 AM',
      '0 9 1 * *': 'Mensual (día 1) 9:00 AM',
      
      // Intervalos
      '*/15 * * * *': 'Cada 15 minutos',
      '*/30 * * * *': 'Cada 30 minutos',
      '0 * * * *': 'Cada hora',
      '0 */2 * * *': 'Cada 2 horas',
      '0 */6 * * *': 'Cada 6 horas',
      '0 */12 * * *': 'Cada 12 horas'
    }
    
    return schedules[cron] || `Cron: ${cron}`
  }

  const formatNextExecution = (nextExecution) => {
    if (!nextExecution) return null
    
    const date = new Date(nextExecution)
    const now = new Date()
    const diffInMs = date - now
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
    
    const dateStr = date.toLocaleDateString('es-CL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
    
    if (diffInMs < 0) {
      return { text: 'Vencida', color: 'error', date: dateStr }
    } else if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
      return { text: `En ${diffInMinutes} min`, color: 'warning', date: dateStr }
    } else if (diffInHours < 24) {
      return { text: `En ${diffInHours}h`, color: 'info', date: dateStr }
    } else if (diffInDays < 7) {
      return { text: `En ${diffInDays} días`, color: 'primary', date: dateStr }
    } else {
      return { text: dateStr, color: 'default', date: dateStr }
    }
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          Error al cargar las configuraciones de notificaciones: {error.message}
        </Alert>
      </Box>
    )
  }

  return (
    <RoleGuard requireAdmin redirectTo404>
      <Box>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            <NotificationsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Notificaciones
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configura y administra las notificaciones automáticas del sistema
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<ScheduleIcon />}
              label="Configuraciones"
              iconPosition="start"
            />
            <Tab
              icon={<HistoryIcon />}
              label="Historial"
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Box>
            {/* Quick Actions */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Acciones Rápidas
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateConfig}
                  disabled={isCreating}
                >
                  Nueva Configuración
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={handleSendWeeklyReminder}
                  disabled={isSendingWeekly}
                  color="primary"
                >
                  Enviar Recordatorio Semanal
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ErrorIcon />}
                  onClick={handleSendOverdueAlert}
                  disabled={isSendingOverdue}
                  color="error"
                >
                  Enviar Alerta de Vencidas
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EventIcon />}
                  onClick={handleSendMonthlyReport}
                  disabled={isSendingMonthly}
                  color="info"
                >
                  Enviar Reporte Mensual
                </Button>
              </Box>
            </Paper>

            {/* Configurations List */}
            <Grid container spacing={3}>
              {isLoading ? (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography>Cargando configuraciones...</Typography>
                  </Paper>
                </Grid>
              ) : configs.length === 0 ? (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      No hay configuraciones de notificaciones
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      Crea tu primera configuración para comenzar a recibir notificaciones automáticas
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleCreateConfig}
                    >
                      Crear Primera Configuración
                    </Button>
                  </Paper>
                </Grid>
              ) : (
                configs.map((config) => (
                  <Grid item xs={12} md={6} lg={4} key={config.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Chip
                            label={getNotificationTypeLabel(config.notificationType)}
                            color={getNotificationTypeColorWithTheme(config.notificationType, theme)}
                            size="small"
                            sx={{ 
                              mb: 1,
                              ...(theme.palette.mode === 'dark' && {
                                backgroundColor: theme.palette[getNotificationTypeColor(config.notificationType)]?.dark,
                                color: theme.palette[getNotificationTypeColor(config.notificationType)]?.contrastText
                              })
                            }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {config.isScheduled ? (
                              <Tooltip title="Programada y activa">
                                <CheckIcon color="success" fontSize="small" />
                              </Tooltip>
                            ) : (
                              <Tooltip title="No programada">
                                <ErrorIcon color="error" fontSize="small" />
                              </Tooltip>
                            )}
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={config.active}
                                  onChange={() => handleToggleConfig(config)}
                                  size="small"
                                  disabled={isUpdating}
                                />
                              }
                              label=""
                              sx={{ ml: 1, mr: 0 }}
                            />
                          </Box>
                        </Box>

                        {/* Description */}
                        {config.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {config.description}
                          </Typography>
                        )}

                        {/* Schedule */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <ScheduleIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {formatCronSchedule(config.cronSchedule)}
                          </Typography>
                        </Box>

                        {/* Next Execution */}
                        {config.nextExecution && config.active && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Box>
                              <Chip
                                label={formatNextExecution(config.nextExecution)?.text}
                                color={formatNextExecution(config.nextExecution)?.color}
                                size="small"
                                sx={{ 
                                  mr: 1,
                                  ...(theme.palette.mode === 'dark' && {
                                    backgroundColor: theme.palette[formatNextExecution(config.nextExecution)?.color]?.dark,
                                    color: theme.palette[formatNextExecution(config.nextExecution)?.color]?.contrastText
                                  })
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatNextExecution(config.nextExecution)?.date}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Recipients */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {config.recipients.length} destinatario{config.recipients.length !== 1 ? 's' : ''}
                          </Typography>
                        </Box>

                        {/* Recipients List */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {config.recipients.slice(0, 2).map((email, index) => (
                            <Chip
                              key={index}
                              label={email}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.7rem',
                                ...(theme.palette.mode === 'dark' && {
                                  borderColor: theme.palette.divider,
                                  color: theme.palette.text.primary,
                                  '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                    borderColor: theme.palette.divider
                                  }
                                })
                              }}
                            />
                          ))}
                          {config.recipients.length > 2 && (
                            <Chip
                              label={`+${config.recipients.length - 2} más`}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.7rem',
                                ...(theme.palette.mode === 'dark' && {
                                  borderColor: theme.palette.divider,
                                  color: theme.palette.text.primary,
                                  '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                    borderColor: theme.palette.divider
                                  }
                                })
                              }}
                            />
                          )}
                        </Box>
                      </CardContent>

                      <Divider />

                      <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                        <Box>
                          <Tooltip title="Editar configuración">
                            <IconButton
                              size="small"
                              onClick={() => handleEditConfig(config)}
                              disabled={isUpdating}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar configuración">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteConfig(config)}
                              disabled={isDeleting}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Tooltip title="Enviar prueba">
                          <Button
                            size="small"
                            startIcon={<PlayIcon />}
                            onClick={() => handleSendTest(config)}
                            disabled={isSendingTest}
                            variant="outlined"
                          >
                            Probar
                          </Button>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        )}

        {activeTab === 1 && (
          <NotificationLogs />
        )}

        {/* Form Dialog */}
        <Dialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingConfig ? 'Editar Configuración' : 'Nueva Configuración de Notificación'}
          </DialogTitle>
          <DialogContent>
            <NotificationForm
              initialData={editingConfig}
              onSubmit={handleFormSubmit}
              onCancel={() => setFormOpen(false)}
              isSubmitting={isCreating || isUpdating}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
        >
          <DialogTitle>Confirmar Eliminación</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Estás seguro de que deseas eliminar la configuración "{getNotificationTypeLabel(configToDelete?.notificationType)}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Esta acción no se puede deshacer y detendrá todas las notificaciones programadas de este tipo.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
              disabled={isDeleting}
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </RoleGuard>
  )
}

export default NotificationsPage
